import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Baby, School, GraduationCap, ArrowLeft, Gift, Play, X, User, Youtube, BookOpen, Copy, Check, Users, Video } from 'lucide-react';
import { useGame } from '../context/GameContext';
import { PointsButton } from '../components/RewardsWidgets';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgeSelection = () => {
  const navigate = useNavigate();
  const { setSelectedAge, playerName, savedProgress, restoreProgress, clearProgress, loadProgress } = useGame();
  const [showContinue, setShowContinue] = useState(false);
  const [linkCode, setLinkCode] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch link code
  useEffect(() => {
    const fetchLinkCode = async () => {
      if (playerName) {
        try {
          const response = await axios.get(`${API}/player/${playerName}/link-code`);
          if (response.data.link_code) {
            setLinkCode(response.data.link_code);
          }
        } catch (error) {
          console.error('Error fetching link code:', error);
        }
      }
    };
    fetchLinkCode();
  }, [playerName]);

  const copyLinkCode = () => {
    if (linkCode) {
      navigator.clipboard.writeText(linkCode).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => {
        const textArea = document.createElement('textarea');
        textArea.value = linkCode;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  useEffect(() => {
    // Load progress from backend when component mounts
    const checkProgress = async () => {
      const progress = await loadProgress();
      if (progress) {
        setShowContinue(true);
      }
    };
    checkProgress();
  }, [loadProgress]);

  // Check if there's saved progress
  useEffect(() => {
    if (savedProgress && savedProgress.ageGroup) {
      setShowContinue(true);
    }
  }, [savedProgress]);

  const handleContinue = () => {
    if (savedProgress) {
      restoreProgress(savedProgress);
      // Navigate to the appropriate page based on saved progress
      if (savedProgress.gameFormat) {
        navigate(`/game/${savedProgress.gameFormat}`);
      } else if (savedProgress.subject) {
        navigate('/game-format');
      } else if (savedProgress.ageGroup === 'preschool') {
        navigate('/preschool-topics');
      } else {
        navigate('/subject-selection');
      }
    }
  };

  const handleStartFresh = () => {
    clearProgress();
    setShowContinue(false);
  };

  const ageGroups = [
    {
      id: 'preschool',
      label: 'Little Explorer',
      age: '3-5 years',
      icon: Baby,
      color: 'from-pink-400 to-rose-400',
      description: 'Colors, shapes, and simple counting!'
    },
    {
      id: 'elementary',
      label: 'Young Scholar',
      age: '6-8 years',
      icon: School,
      color: 'from-green-400 to-teal-400',
      description: 'Reading, math, and fun challenges!'
    },
    {
      id: 'advanced',
      label: 'Smart Champion',
      age: '9-12 years',
      icon: GraduationCap,
      color: 'from-blue-400 to-indigo-400',
      description: 'Advanced topics and critical thinking!'
    }
  ];

  const handleSelect = (ageGroup) => {
    setSelectedAge(ageGroup);
    // For preschool (3-5), go to special preschool topics page
    if (ageGroup === 'preschool') {
      navigate('/preschool-topics');
    } else {
      navigate('/subject-selection');
    }
  };

  // Get readable name for saved progress
  const getProgressDescription = () => {
    if (!savedProgress) return '';
    const ageNames = { preschool: 'Little Explorer', elementary: 'Young Scholar', advanced: 'Smart Champion' };
    const parts = [];
    if (savedProgress.ageGroup) parts.push(ageNames[savedProgress.ageGroup] || savedProgress.ageGroup);
    if (savedProgress.subject) parts.push(savedProgress.subject);
    if (savedProgress.gameFormat) parts.push(savedProgress.gameFormat);
    return parts.join(' → ');
  };

  const playerAvatar = localStorage.getItem('playerAvatar') || '🧒';

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
    }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-lg font-semibold hover:scale-110 transition-transform"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-2 md:gap-3">
            {/* Link Code Display */}
            {linkCode && (
              <div 
                onClick={copyLinkCode}
                className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-400 to-orange-400 text-white font-bold rounded-full cursor-pointer hover:scale-105 transition-transform shadow-lg"
                title="Share this code with your parent!"
              >
                <Users className="w-4 h-4" />
                <span className="font-mono text-sm md:text-base tracking-wider">{linkCode}</span>
                {copied ? (
                  <Check className="w-4 h-4 text-green-200" />
                ) : (
                  <Copy className="w-4 h-4 opacity-75" />
                )}
              </div>
            )}
            <PointsButton />
            {/* Profile Button */}
            <Button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <span className="text-lg">{playerAvatar}</span>
              <User className="w-4 h-4 hidden md:block" />
              <span className="hidden md:inline">My Profile</span>
            </Button>
            {/* Rewards Button */}
            <Button
              onClick={() => navigate('/rewards')}
              className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-full hover:scale-105 transition-transform shadow-lg"
            >
              <Gift className="w-5 h-5" />
              <span className="hidden md:inline">Rewards</span>
            </Button>
          </div>
        </div>

        {/* Continue Playing Banner */}
        {showContinue && savedProgress && (
          <Card className="mb-8 p-4 md:p-6 bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-xl animate-pulse-slow" style={{ borderRadius: '20px' }}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-4xl">🎮</div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold">Continue Where You Left Off!</h3>
                  <p className="text-white/80">{getProgressDescription()}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleStartFresh}
                  variant="outline"
                  className="bg-white/20 border-white/50 text-white hover:bg-white/30"
                  style={{ borderRadius: '12px' }}
                >
                  <X className="w-4 h-4 mr-1" />
                  Start Fresh
                </Button>
                <Button
                  onClick={handleContinue}
                  className="bg-white text-green-600 font-bold hover:bg-white/90 hover:scale-105 transition-transform"
                  style={{ borderRadius: '12px' }}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Continue Playing!
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="text-center mb-12 bounce-in">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-4">
            Hi {playerName}! How Old Are You?
          </h1>
          <p className="text-2xl text-gray-700">Pick your learning level!</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {ageGroups.map((group, index) => {
            const IconComponent = group.icon;
            return (
              <Card
                key={group.id}
                className="cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden slide-up"
                style={{
                  animationDelay: `${index * 0.1}s`,
                  borderRadius: '28px'
                }}
                onClick={() => handleSelect(group.id)}
              >
                <div className={`h-48 bg-gradient-to-br ${group.color} flex items-center justify-center relative overflow-hidden`}>
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                    <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12"></div>
                  </div>
                  <IconComponent className="w-24 h-24 text-white float-animation" />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{group.label}</h3>
                  <p className="text-lg font-semibold text-gray-600 mb-3">{group.age}</p>
                  <p className="text-gray-600">{group.description}</p>
                  <Button
                    className="w-full mt-6 py-5 text-lg font-bold transition-all duration-300"
                    style={{
                      background: `linear-gradient(135deg, ${group.color})`,
                      borderRadius: '14px'
                    }}
                  >
                    Choose This Level
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* YouTube Shorts, Kids Books, and Kids Videos Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Signature Kids YouTube Shorts */}
          <div
            className="cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden slide-up bg-white"
            style={{
              animationDelay: '0.3s',
              borderRadius: '28px',
              textDecoration: 'none'
            }}
          >
            <a 
              href="https://www.youtube.com/@signaturekids3611/shorts"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="h-48 bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                  <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12"></div>
                </div>
                <Youtube className="w-24 h-24 text-white float-animation" />
              </div>
            </a>
            <div className="p-6 bg-white" style={{ borderRadius: '0 0 28px 28px' }}>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Signature Kids YouTube Shorts</h3>
              <p className="text-lg font-semibold text-gray-600 mb-3">Watch & Learn!</p>
              <p className="text-gray-600">Fun educational videos for kids</p>
              
              {/* Copy Link Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const url = 'https://www.youtube.com/@signaturekids3611/shorts';
                  
                  const textArea = document.createElement('textarea');
                  textArea.value = url;
                  textArea.style.position = 'fixed';
                  textArea.style.left = '-9999px';
                  textArea.style.top = '-9999px';
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  
                  try {
                    document.execCommand('copy');
                    alert('✅ Link copied!\n\nPaste this in your browser:\n' + url);
                  } catch (err) {
                    prompt('Copy this link:', url);
                  }
                  
                  document.body.removeChild(textArea);
                }}
                className="w-full mt-4 py-3 text-lg font-bold transition-all duration-300 text-red-600 text-center border-2 border-red-500 hover:bg-red-50"
                style={{
                  borderRadius: '14px'
                }}
              >
                📋 Copy Link
              </button>
              
              {/* Watch Now Button */}
              <a 
                href="https://www.youtube.com/@signaturekids3611/shorts"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full mt-3 py-5 text-lg font-bold transition-all duration-300 text-white text-center"
                style={{
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  borderRadius: '14px'
                }}
              >
                Watch Now
              </a>
            </div>
          </div>

          {/* Kids Books */}
          <Card
            className="cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden slide-up"
            style={{
              animationDelay: '0.4s',
              borderRadius: '28px'
            }}
            onClick={() => navigate('/kids-books')}
          >
            <div className="h-48 bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12"></div>
              </div>
              <BookOpen className="w-24 h-24 text-white float-animation" />
            </div>
            <div className="p-6 bg-white" style={{ borderRadius: '0 0 28px 28px' }}>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Kids Books</h3>
              <p className="text-lg font-semibold text-gray-600 mb-3">Read & Explore!</p>
              <p className="text-gray-600">Discover amazing stories for young readers</p>
              
              <div
                className="w-full mt-6 py-5 text-lg font-bold transition-all duration-300 text-white text-center"
                style={{
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  borderRadius: '14px'
                }}
              >
                📚 Browse Books
              </div>
            </div>
          </Card>

          {/* Kids Videos - NEW */}
          <Card
            className="cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl overflow-hidden slide-up"
            style={{
              animationDelay: '0.5s',
              borderRadius: '28px'
            }}
            onClick={() => navigate('/kids-videos')}
          >
            <div className="h-48 bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
                <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12"></div>
              </div>
              <Video className="w-24 h-24 text-white float-animation" />
            </div>
            <div className="p-6 bg-white" style={{ borderRadius: '0 0 28px 28px' }}>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Kids Videos</h3>
              <p className="text-lg font-semibold text-gray-600 mb-3">Watch & Learn!</p>
              <p className="text-gray-600">Fun educational videos for little learners</p>
              
              <div
                className="w-full mt-6 py-5 text-lg font-bold transition-all duration-300 text-white text-center"
                style={{
                  background: 'linear-gradient(135deg, #a855f7, #ec4899)',
                  borderRadius: '14px'
                }}
              >
                🎬 Watch Videos
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AgeSelection;
